import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

export interface ChatMessage {
    id: string;
    sender: 'bot' | 'user';
    text: string;
    timestamp: Date;
    options?: ChatOption[];
    actions?: ChatAction[];
    category?: string;
}

export interface ChatOption {
    id: string;
    label: string;
    icon?: string;
    category?: string;
}

export interface ChatAction {
    label: string;
    type: 'route' | 'whatsapp' | 'reset' | 'category';
    payload?: string;
    icon?: string;
}

export interface FAQItem {
    id: string;
    category: string;
    question: string;
    icon?: string;
    keywords: string[];
    answer: string;
    actions?: ChatAction[];
    followUps?: string[];
}

@Component({
    selector: 'app-chatbot',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './chatbot.component.html',
    styleUrls: ['./chatbot.component.scss']
})
export class ChatbotComponent implements OnInit, OnDestroy {
    @ViewChild('chatMessagesContainer') private chatMessagesContainer!: ElementRef;

    // Visibility States
    showTriggerButton: boolean = true;
    showGreetingBubble: boolean = true;
    isOpenChat: boolean = false;
    isTyping: boolean = false;
    hasNewMessageBadge: boolean = true;
    hasAutoOpenedOnSection3: boolean = false;

    // Chat State
    userInput: string = '';
    selectedCategory: string = 'all';
    messages: ChatMessage[] = [];

    // WhatsApp Contact
    readonly whatsappNumber: string = '6282260084847';

    // Categories
    readonly categories = [
        { id: 'all', label: 'Semua Topik' },
        { id: 'photobooth', label: 'Photobooth & 360' },
        { id: 'undangan', label: 'Undangan Digital' },
        { id: 'order', label: 'Cara Pesan & Bayar' },
        { id: 'ticketing', label: 'Smart Ticketing & EO' },
        { id: 'rental', label: 'Sewa Alat' }
    ];

    // FAQ Knowledge Base
    readonly faqDatabase: FAQItem[] = [
        {
            id: 'photobooth_order',
            category: 'photobooth',
            question: 'Bagaimana cara booking paket Photobooth / 360 Videobooth?',
            keywords: ['booking photobooth', 'pesan photobooth', 'sewa photobooth', '360 videobooth', 'cara sewa foto', 'order photobooth'],
            answer: `Untuk memesan layanan Photobooth atau 360 Videobooth di Visidea Studio, ikuti langkah mudah berikut:\n\n1. 📋 **Pilih Paket:** Buka halaman paket untuk melihat pilihan (Unlimited Print, Strip Photo, atau 360 Spin).\n2. 📅 **Cek Ketersediaan Tanggal:** Hubungi admin kami untuk memastikan tanggal acara Anda masih tersedia.\n3. 🎨 **Kustomisasi Desain:** Kirimkan logo/tema/template frame yang Anda inginkan (kami sediakan revisi free).\n4. 💰 **Konfirmasi & DP:** Lakukan pembayaran uang muka untuk mengunci jadwal acara Anda. Tim kami siap hadir 1-2 jam sebelum acara dimulai!`,
            actions: [
                { label: 'Lihat Paket Photobooth', type: 'route', payload: '/photobooth', icon: '📸' },
                { label: 'Chat Admin WhatsApp', type: 'whatsapp', payload: 'Halo Admin Visidea, saya ingin konsultasi booking paket Photobooth.', icon: '💬' }
            ],
            followUps: ['photobooth_diff', 'payment_method', 'order_how']
        },
        {
            id: 'photobooth_diff',
            category: 'photobooth',
            question: 'Apa perbedaan Photobooth biasa dan 360 Videobooth?',
            keywords: ['beda photobooth', 'perbedaan photobooth', '360', 'spin 360', 'videobooth'],
            answer: `Berikut perbedaannya kak:\n\n• **Photobooth Klasik / Strip:** Tamu berfoto di depan backdrop dengan properti seru, dan foto fisik langsung tercetak dalam hitungan detik + softfile via QR code.\n\n• **360 Videobooth:** Tamu berdiri di atas platform berputar, kamera merekam video dinamis dengan efek slow-motion & musik custom, menghasilkan video aesthetic berbingkai yang langsung bisa diunduh via QR dan dibagikan ke media sosial!`,
            actions: [
                { label: 'Lihat Demo 360 Videobooth', type: 'route', payload: '/videobooth', icon: '🎥' },
                { label: 'Tanya Harga Paket', type: 'whatsapp', payload: 'Halo, saya ingin tanya harga paket 360 Videobooth.', icon: '💬' }
            ],
            followUps: ['photobooth_order', 'payment_method']
        },
        {
            id: 'undangan_duration',
            category: 'undangan',
            question: 'Berapa lama proses pembuatan Undangan Digital?',
            keywords: ['lama pembuatan', 'durasi undangan', 'berapa hari', 'proses undangan', 'pengerjaan'],
            answer: `Proses pembuatan Undangan Digital di Visidea Studio sangat cepat ⚡:\n\n• **Paket Standar / Express:** 1 - 2 hari kerja setelah data mempelai & foto lengkap dikirimkan.\n• **Kustom Desain Khusus:** 2 - 4 hari kerja tergantung kompleksitas fitur.\n• **Revisi:** Kami menyediakan sesi preview & revisi teks/foto hingga Anda puas sebelum disebarkan ke tamu undangan!`,
            actions: [
                { label: 'Lihat Katalog Desain', type: 'route', payload: '/digital-invitation', icon: '💌' },
                { label: 'Konsultasi Undangan', type: 'whatsapp', payload: 'Halo Visidea, saya ingin info pembuatan undangan digital.', icon: '💬' }
            ],
            followUps: ['undangan_features', 'undangan_custom']
        },
        {
            id: 'undangan_features',
            category: 'undangan',
            question: 'Fitur apa saja yang ada di Undangan Digital Visidea?',
            keywords: ['fitur undangan', 'fasilitas undangan', 'kelebihan undangan', 'isi undangan'],
            answer: `Undangan Digital Visidea dilengkapi fitur modern & premium:\n\n✅ Nama Tamu Otomatis (Custom Guest Greeting)\n✅ Countdown Acara & Integrasi Kalender Google\n✅ Peta Lokasi Interaktif (Google Maps Navigasi)\n✅ Galeri Foto & Video Prewedding High-Res\n✅ Konfirmasi Kehadiran (RSVP) & Buku Tamu Digital\n✅ Background Musik Romantis Pilihan Anda\n✅ Amplop Digital (QRIS & Transfer Bank Otomatis)\n✅ Fitur Cerita Cinta (Love Story) & Doa/Ucapan Real-time`,
            actions: [
                { label: 'Pilih Tema Undangan', type: 'route', payload: '/digital-invitation', icon: '💌' }
            ],
            followUps: ['undangan_duration', 'undangan_custom']
        },
        {
            id: 'undangan_custom',
            category: 'undangan',
            question: 'Apakah bisa custom desain undangan sesuai tema pernikahan saya?',
            keywords: ['custom tema', 'request desain', 'custom desain', 'warna undangan', 'tema khusus'],
            answer: `Tentu saja bisa! 🎉 Anda dapat menyesuaikan warna tema, font, ornamen, foto, tata letak, hingga menyertakan filter Instagram / video khusus. Tim desainer kami siap mewujudkan konsep pernikahan impian Anda.`,
            actions: [
                { label: 'Hubungi Tim Desainer', type: 'whatsapp', payload: 'Halo Visidea, saya ingin custom desain undangan digital sesuai tema acara saya.', icon: '🎨' }
            ],
            followUps: ['undangan_duration', 'payment_method']
        },
        {
            id: 'order_how',
            category: 'order',
            question: 'Bagaimana cara melakukan pemesanan (Order)?',
            keywords: ['cara order', 'cara pesan', 'langkah pemesanan', 'alur order', 'checkout'],
            answer: `Langkah pemesanan sangat mudah:\n\n1. Pilih layanan atau paket yang Anda butuhkan di katalog website Visidea.\n2. Klik tombol **"Pesan Sekarang"** atau masukkan ke keranjang penawaran.\n3. Isi data diri & detail acara Anda.\n4. Konfirmasi pesanan via WhatsApp dengan Customer Service kami untuk finalisasi jadwal & nota resmi e-invoice.`,
            actions: [
                { label: 'Lihat Semua Layanan', type: 'route', payload: '/package', icon: '📦' },
                { label: 'Hubungi CS WhatsApp', type: 'whatsapp', payload: 'Halo CS Visidea, saya ingin memesan layanan.', icon: '💬' }
            ],
            followUps: ['payment_method', 'photobooth_order']
        },
        {
            id: 'payment_method',
            category: 'order',
            question: 'Metode pembayaran apa saja yang tersedia?',
            keywords: ['metode pembayaran', 'cara bayar', 'transfer', 'qris', 'rekening', 'cicilan', 'dp'],
            answer: `Kami mendukung berbagai metode pembayaran aman & terverifikasi:\n\n💳 **Transfer Bank Virtual Account & Manual:** BCA, Mandiri, BNI, BRI\n📱 **E-Wallet & QRIS:** GoPay, OVO, Dana, ShopeePay, LinkAja\n\n📌 *Ketentuan Pembayaran:* Booking tanggal cukup dengan DP 30% - 50%, dan pelunasan dapat dilakukan sebelum atau pada hari H acara.`,
            actions: [
                { label: 'Tanya Rekening Pembayaran', type: 'whatsapp', payload: 'Halo, saya ingin konfirmasi nomor rekening pembayaran Visidea.', icon: '💬' }
            ],
            followUps: ['order_how', 'photobooth_order']
        },
        {
            id: 'ticketing_ezgate',
            category: 'ticketing',
            question: 'Apa itu Smart Ticketing EZGate & Guest Book Digital?',
            keywords: ['smart ticketing', 'ezgate', 'buku tamu digital', 'guest book', 'gate check in', 'scan qr tiket'],
            answer: `**EZGate & Digital Guest Book** adalah solusi manajemen event modern dari Visidea:\n\n🎫 **Smart Ticketing & EZGate:** Gate barcode/QR otomatis untuk check-in ribuan pengunjung dalam hitungan detik tanpa antre panjang.\n📖 **Digital Guest Book:** Tamu pernikahan/acara melakukan registrasi scan QR di meja resepsionis, otomatis print label nama & sinkronisasi souvenir secara real-time!`,
            actions: [
                { label: 'Lihat Smart Ticketing', type: 'route', payload: '/smart-ticketing', icon: '⚡' },
                { label: 'Lihat Guest Book Digital', type: 'route', payload: '/guest-book', icon: '📖' }
            ],
            followUps: ['eo_partnership', 'rental_equipment']
        },
        {
            id: 'rental_equipment',
            category: 'rental',
            question: 'Bagaimana prosedur sewa alat (Equipment Rental)?',
            keywords: ['sewa alat', 'rental kamera', 'sewa lighting', 'rental sound', 'equipment', 'sewa tv'],
            answer: `Visidea Studio menyediakan persewaan alat profesional untuk event & produksi:\n\n📷 Kamera & Lensa Cinema / Mirrorless\n💡 Lighting Studio, RGB Tube, Softbox\n🔊 Sound System & Wireless Clip-on Mic\n📺 LED Screen, TV Monitor & Tripod\n\n*Prosedur Sewa:* Hubungi admin dengan menyebutkan list alat, tanggal sewa, serta lokasi pengiriman. Kami melayani antar-jemput dan pendampingan operator teknis.`,
            actions: [
                { label: 'Katalog Sewa Alat', type: 'route', payload: '/equipment-rental', icon: '🎬' },
                { label: 'Tanya Ketersediaan Alat', type: 'whatsapp', payload: 'Halo, saya ingin menanyakan ketersediaan rental alat event.', icon: '💬' }
            ],
            followUps: ['order_how', 'payment_method']
        },
        {
            id: 'eo_partnership',
            category: 'ticketing',
            question: 'Bagaimana cara menjadi Mitra / Kolaborasi EO dengan Visidea?',
            keywords: ['mitra', 'partnership', 'kolaborasi', 'event organizer', 'komisi wo', 'wedding organizer'],
            answer: `Kami membuka peluang kemitraan spesial untuk Wedding Organizer (WO), Event Planner, Fotografer, dan Vendor Event dengan keuntungan menarik:\n\n🌟 Harga khusus vendor & komisi kemitraan menguntungkan\n🌟 Prioritas jadwal dan dukungan teknisi on-site\n🌟 Akses materi marketing & portfolio bersama\n\nSilakan isi formulir mitra di website atau hubungi tim partnership kami!`,
            actions: [
                { label: 'Halaman Kemitraan', type: 'route', payload: '/mitra', icon: '🤝' },
                { label: 'Hubungi Tim Partnership', type: 'whatsapp', payload: 'Halo Visidea, saya dari tim WO/EO ingin berdiskusi mengenai kemitraan.', icon: '🤝' }
            ],
            followUps: ['photobooth_order', 'order_how']
        },
        {
            id: 'contact_human',
            category: 'all',
            question: 'Saya ingin berbicara langsung dengan Customer Service (CS)',
            keywords: ['cs', 'admin', 'whatsapp', 'kontak', 'hubungi admin', 'telepon', 'lokasi', 'alamat'],
            answer: `Tim Customer Service Visidea Studio selalu siap melayani Anda! 😊\n\n📍 **Alamat Studio:** Visidea Studio\n🕒 **Jam Operasional:** Setiap Hari (08.00 - 22.00 WIB)\n📞 **WhatsApp CS:** +62 822-6008-4847\n\nKlik tombol di bawah ini untuk langsung terhubung via WhatsApp:`,
            actions: [
                { label: 'Chat WhatsApp CS Sekarang', type: 'whatsapp', payload: 'Halo CS Visidea, saya ingin konsultasi langsung dengan admin.', icon: '💬' }
            ],
            followUps: ['photobooth_order', 'undangan_duration', 'payment_method']
        }
    ];

    private routerSub: any;
    private section3Observer: IntersectionObserver | null = null;

    private scrollListener = () => {
        this.showGreetingBubble = this.isHomePage();
        this.checkSection3Visibility();
        this.cdr.detectChanges();
    };

    constructor(private cdr: ChangeDetectorRef, private router: Router) { }

    private isHomePage(): boolean {
        // Check if current route is home page
        if (typeof window !== 'undefined') {
            const pathname = window.location.pathname;
            if (pathname === '/' || pathname === '' || pathname === '/home') return true;
        }
        const currentUrl = this.router.url.split(/[?#]/)[0];
        return currentUrl === '/' || currentUrl === '/home' || currentUrl === '';
    }

    ngOnInit(): void {
        this.initChat();

        // Listen for scroll on window & document with capture
        if (typeof window !== 'undefined') {
            window.addEventListener('scroll', this.scrollListener, { passive: true, capture: true });
            document.addEventListener('scroll', this.scrollListener, { passive: true, capture: true });
            window.addEventListener('touchmove', this.scrollListener, { passive: true, capture: true });
        }

        // Router subscription to re-check & observe when navigating
        this.routerSub = this.router.events.subscribe(() => {
            setTimeout(() => {
                this.showGreetingBubble = this.isHomePage();
                if (this.isHomePage()) {
                    this.setupSection3Observer();
                    this.checkSection3Visibility();
                }
                this.cdr.detectChanges();
            }, 300);
        });

        // Ensure greeting bubble is shown after 800ms only if on home page
        setTimeout(() => {
            this.showGreetingBubble = this.isHomePage();
            if (this.isHomePage()) {
                this.setupSection3Observer();
                this.checkSection3Visibility();
            }
            this.cdr.detectChanges();
        }, 800);
    }

    ngOnDestroy(): void {
        if (typeof window !== 'undefined') {
            window.removeEventListener('scroll', this.scrollListener);
            document.removeEventListener('scroll', this.scrollListener);
            window.removeEventListener('touchmove', this.scrollListener);
        }
        if (this.routerSub) {
            this.routerSub.unsubscribe();
        }
        if (this.section3Observer) {
            this.section3Observer.disconnect();
            this.section3Observer = null;
        }
    }

    private setupSection3Observer(): void {
        if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
            return;
        }

        if (this.section3Observer) {
            this.section3Observer.disconnect();
            this.section3Observer = null;
        }

        const guestBookSection = document.getElementById('guest-book');
        if (!guestBookSection) {
            setTimeout(() => {
                const el = document.getElementById('guest-book');
                if (el) {
                    this.observeSection3Element(el);
                }
            }, 500);
            return;
        }

        this.observeSection3Element(guestBookSection);
    }

    private observeSection3Element(el: HTMLElement): void {
        this.section3Observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && this.isHomePage() && !this.hasAutoOpenedOnSection3 && !this.isOpenChat) {
                    this.hasAutoOpenedOnSection3 = true;
                    this.openChat();
                    this.cdr.detectChanges();
                }
            });
        }, {
            threshold: [0.1, 0.25],
            rootMargin: '0px 0px -40px 0px'
        });

        this.section3Observer.observe(el);
    }

    @HostListener('window:scroll', [])
    onWindowScroll(): void {
        this.showGreetingBubble = this.isHomePage();
        this.checkSection3Visibility();
    }

    @HostListener('document:scroll', [])
    onDocumentScroll(): void {
        this.showGreetingBubble = this.isHomePage();
        this.checkSection3Visibility();
    }

    checkSection3Visibility(): void {
        // Only auto-open on home page and if not already opened
        if (!this.isHomePage() || this.hasAutoOpenedOnSection3 || this.isOpenChat) {
            return;
        }

        const guestBookSection = document.getElementById('guest-book');
        if (!guestBookSection) {
            return;
        }

        const rect = guestBookSection.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;

        // Check if section is entering viewport (top is within 85% of screen height)
        const isVisible = rect.top < windowHeight * 0.85 && rect.bottom > 80;

        if (isVisible) {
            this.hasAutoOpenedOnSection3 = true;
            this.openChat();
            this.cdr.detectChanges();
        }
    }

    initChat(): void {
        const welcomeMessage: ChatMessage = {
            id: 'welcome-1',
            sender: 'bot',
            text: `Halo kak! Selamat datang di Visidea Studio 👋✨\n\nAda yang bisa kami bantu hari ini? Silakan pilih topik pertanyaan di bawah atau ketik langsung pertanyaan Anda:`,
            timestamp: new Date(),
            options: this.getInitialOptions()
        };

        this.messages = [welcomeMessage];
    }

    getInitialOptions(): ChatOption[] {
        return this.faqDatabase.slice(0, 5).map(faq => ({
            id: faq.id,
            label: faq.question,
            icon: faq.icon,
            category: faq.category
        }));
    }

    toggleChat(): void {
        this.isOpenChat = !this.isOpenChat;
        if (this.isOpenChat) {
            this.hasNewMessageBadge = false;
            setTimeout(() => this.scrollToBottom(), 100);
        }
    }

    openChat(): void {
        this.isOpenChat = true;
        this.hasNewMessageBadge = false;
        setTimeout(() => this.scrollToBottom(), 100);
    }

    closeChat(): void {
        this.isOpenChat = false;
    }

    dismissBubble(event: MouseEvent): void {
        event.stopPropagation();
        this.showGreetingBubble = false;
    }

    resetChat(): void {
        this.messages = [];
        this.userInput = '';
        this.selectedCategory = 'all';
        this.initChat();
    }

    filterCategory(categoryId: string): void {
        this.selectedCategory = categoryId;
    }

    get filteredFaqs(): FAQItem[] {
        if (this.selectedCategory === 'all') {
            return this.faqDatabase;
        }
        return this.faqDatabase.filter(item => item.category === this.selectedCategory);
    }

    selectQuestion(faqId: string): void {
        const faq = this.faqDatabase.find(item => item.id === faqId);
        if (!faq) return;

        // Add user question message
        this.messages.push({
            id: 'user-' + Date.now(),
            sender: 'user',
            text: faq.question,
            timestamp: new Date()
        });

        this.scrollToBottom();

        // Simulate Bot Typing
        this.isTyping = true;
        setTimeout(() => {
            this.isTyping = false;

            // Generate follow-up suggestions if available
            let followUpOptions: ChatOption[] = [];
            if (faq.followUps && faq.followUps.length > 0) {
                followUpOptions = faq.followUps
                    .map(id => this.faqDatabase.find(f => f.id === id))
                    .filter((f): f is FAQItem => !!f)
                    .map(f => ({
                        id: f.id,
                        label: f.question,
                        icon: f.icon,
                        category: f.category
                    }));
            }

            // Add direct CS option if not already there
            if (!followUpOptions.some(o => o.id === 'contact_human') && faq.id !== 'contact_human') {
                const csFaq = this.faqDatabase.find(f => f.id === 'contact_human');
                if (csFaq) {
                    followUpOptions.push({
                        id: csFaq.id,
                        label: 'Hubungi CS WhatsApp',
                        icon: '👩‍💼'
                    });
                }
            }

            this.messages.push({
                id: 'bot-' + Date.now(),
                sender: 'bot',
                text: faq.answer,
                timestamp: new Date(),
                actions: faq.actions,
                options: followUpOptions.length > 0 ? followUpOptions : undefined
            });

            this.scrollToBottom();
            this.cdr.detectChanges();
        }, 650);
    }

    sendMessage(): void {
        const query = this.userInput.trim();
        if (!query) return;

        // User Message
        this.messages.push({
            id: 'user-' + Date.now(),
            sender: 'user',
            text: query,
            timestamp: new Date()
        });

        this.userInput = '';
        this.scrollToBottom();

        // Bot Search & Answer
        this.isTyping = true;
        setTimeout(() => {
            this.isTyping = false;
            this.handleUserQuery(query);
            this.scrollToBottom();
            this.cdr.detectChanges();
        }, 700);
    }

    handleUserQuery(query: string): void {
        const lower = query.toLowerCase();

        // Find match in FAQ database
        const match = this.faqDatabase.find(faq => {
            const inQuestion = faq.question.toLowerCase().includes(lower);
            const inKeywords = faq.keywords.some(k => lower.includes(k) || k.includes(lower));
            return inQuestion || inKeywords;
        });

        if (match) {
            let followUpOptions: ChatOption[] = [];
            if (match.followUps && match.followUps.length > 0) {
                followUpOptions = match.followUps
                    .map(id => this.faqDatabase.find(f => f.id === id))
                    .filter((f): f is FAQItem => !!f)
                    .map(f => ({
                        id: f.id,
                        label: f.question,
                        icon: f.icon
                    }));
            }

            this.messages.push({
                id: 'bot-' + Date.now(),
                sender: 'bot',
                text: match.answer,
                timestamp: new Date(),
                actions: match.actions,
                options: followUpOptions.length > 0 ? followUpOptions : undefined
            });
        } else {
            // Fallback response with search suggestions
            this.messages.push({
                id: 'bot-' + Date.now(),
                sender: 'bot',
                text: `Terima kasih atas pertanyaannya! 🙏\n\nUntuk pertanyaan khusus mengenai *" ${query} "*, tim Customer Service kami siap memberikan informasi detail dan penawaran terbaik untuk Anda. Silakan hubungi admin kami via WhatsApp atau pilih topik populer berikut:`,
                timestamp: new Date(),
                actions: [
                    {
                        label: 'Tanya Langsung ke CS WhatsApp',
                        type: 'whatsapp',
                        payload: `Halo CS Visidea, saya ingin bertanya mengenai: ${query}`,
                        icon: '💬'
                    }
                ],
                options: this.getInitialOptions().slice(0, 4)
            });
        }
    }

    handleAction(action: ChatAction): void {
        if (action.type === 'route' && action.payload) {
            this.router.navigateByUrl(action.payload);
        } else if (action.type === 'whatsapp') {
            const message = encodeURIComponent(action.payload || 'Halo Visidea Studio, saya ingin bertanya seputar layanan Anda.');
            window.open(`https://wa.me/${this.whatsappNumber}?text=${message}`, '_blank');
        } else if (action.type === 'reset') {
            this.resetChat();
        }
    }

    openWhatsAppDirect(): void {
        const message = encodeURIComponent('Halo, saya tertarik untuk memesan PhotoBooth. Bisa bantu saya dengan informasi lebih lanjut mengenai paket ini? Terima kasih!');
        window.open(`https://wa.me/${this.whatsappNumber}?text=${message}`, '_blank');
    }

    private scrollToBottom(): void {
        try {
            if (this.chatMessagesContainer) {
                this.chatMessagesContainer.nativeElement.scrollTop = this.chatMessagesContainer.nativeElement.scrollHeight;
            }
        } catch (err) { }
    }
}
